import React, { useState, useEffect } from "react";
import { sendMessage, registerAdminDataHandler } from "../services/websocket";
import "./AdminPanel.css";

const TRADER_IDS = Array.from({ length: 20 }, (_, i) => String.fromCharCode(97 + i));
const ROLES = ["oracle", "seer", "pessimist", "early_game_specialist", "late_game_specialist"];

function AdminPanel({ tradingStatus }) {
  const [expanded, setExpanded] = useState(false);
  const [duration, setDuration] = useState(300);
  const [symbols, setSymbols] = useState("A,B,C,D,E");
  const [runNumber, setRunNumber] = useState(1);
  const [drawInterval, setDrawInterval] = useState(15);
  const [settlementInput, setSettlementInput] = useState("");
  const [roleAssignments, setRoleAssignments] = useState({});
  const [miniCaseSymbol, setMiniCaseSymbol] = useState("VWAP");
  const [miniCaseDuration, setMiniCaseDuration] = useState(600);
  const [leaderboard, setLeaderboard] = useState(null);
  const [viewedPortfolio, setViewedPortfolio] = useState(null);
  const [portfolioUserId, setPortfolioUserId] = useState("a");
  const [teams, setTeams] = useState([]);
  const [pendingTeams, setPendingTeams] = useState({}); // {team_id: [member1, member2]}
  const [botRunning, setBotRunning] = useState(true);
  const [quarterNum, setQuarterNum] = useState(1);
  const [combinedLeaderboard, setCombinedLeaderboard] = useState(null);
  const [log, setLog] = useState([]);

  useEffect(() => {
    const unregister = registerAdminDataHandler((message) => {
      if (message.command === "leaderboard_data") {
        setLeaderboard(message.leaderboard);
        addLog(`Leaderboard loaded: ${message.leaderboard.length} teams`);
      } else if (message.command === "admin_portfolio") {
        setViewedPortfolio(message);
        addLog(`Portfolio loaded: ${message.name} (${message.user_id})`);
      } else if (message.command === "teams_data") {
        setTeams(message.teams || []);
        const pending = {};
        for (const team of (message.teams || [])) {
          pending[team.team_id] = [...(team.members || [])];
        }
        setPendingTeams(pending);
        addLog(`Teams loaded: ${(message.teams || []).length} teams`);
      } else if (message.command === "bot_status") {
        setBotRunning(message.running);
        addLog(message.message);
      } else if (message.command === "quarter_saved") {
        addLog(message.message);
      } else if (message.command === "combined_leaderboard") {
        setCombinedLeaderboard(message);
        addLog(`Combined leaderboard: Q${message.quarters.join(",")} (${message.leaderboard.length} teams)`);
      }
    });
    return () => unregister();
  }, []);

  const addLog = (msg) => {
    setLog((prev) => [`[${new Date().toLocaleTimeString()}] ${msg}`, ...prev].slice(0, 50));
  };

  const send = (command, data = {}) => {
    const ok = sendMessage({ command, ...data });
    if (ok) addLog(`Sent: ${command}`);
    else addLog(`Failed to send: ${command} (not connected)`);
  };

  const handleLoadCase = () => send("load_case", { run_number: runNumber });

  const handleAssignRoles = () => {
    const assignments = {};
    for (const [userId, role] of Object.entries(roleAssignments)) {
      if (role) assignments[userId] = role;
    }
    if (Object.keys(assignments).length === 0) {
      addLog("No roles assigned");
      return;
    }
    send("assign_roles", { assignments });
  };

  const handleSetSymbols = () => {
    const list = symbols.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) {
      addLog("No symbols provided");
      return;
    }
    send("set_symbols", { symbols: list });
  };

  const handleStartTrading = () => send("start_trading", { duration: duration || null });
  const handleEndTrading = () => send("end_trading");
  const handleStartDraws = () => send("start_draws", { interval: drawInterval });
  const handleStopDraws = () => send("stop_draws");
  const handleResetSession = () => send("reset_session");

  const handleSettle = () => {
    const values = {};
    const parts = settlementInput.split(/[\s,]+/);
    for (const part of parts) {
      const [sym, val] = part.split("=");
      if (sym && val && !isNaN(Number(val))) {
        values[sym.trim()] = Number(val.trim());
      }
    }
    if (Object.keys(values).length === 0) {
      addLog("Invalid settlement format. Use: A=70 B=30");
      return;
    }
    send("set_settlement", { values });
  };

  const handleSettleCase = () => send("settle_case");
  const handleLoadMiniCase = () => send("load_mini_case", { symbol: miniCaseSymbol, duration: miniCaseDuration });
  const handleSettleVwap = () => send("settle_vwap");
  const handleGetLeaderboard = () => send("get_leaderboard");
  const handleGetPortfolio = () => send("get_portfolio", { user_id: portfolioUserId });

  const handleGetTeams = () => send("get_teams");

  const handleSaveTeams = () => {
    const teams_data = {};
    for (const [teamId, members] of Object.entries(pendingTeams)) {
      const team = teams.find((t) => t.team_id === teamId);
      teams_data[teamId] = { name: team?.name || teamId, members };
    }
    send("set_teams", { teams: teams_data });
  };

  const handleTeamMemberChange = (teamId, idx, newValue) => {
    setPendingTeams((prev) => {
      const updated = [...(prev[teamId] || [])];
      updated[idx] = newValue;
      return { ...prev, [teamId]: updated };
    });
  };

  useEffect(() => {
    if (expanded) {
      send("get_teams");
    }
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!expanded) {
    return (
      <button
        className="admin-toggle"
        onClick={() => setExpanded(true)}
        style={{ position: "fixed", bottom: "80px", left: "20px", width: "fit-content" }}
      >
        Admin Panel
      </button>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h3>Admin Panel</h3>
        <button className="admin-close" onClick={() => setExpanded(false)}>×</button>
      </div>

      <div className="admin-status">
        Trading: {tradingStatus.running ? "LIVE" : "STOPPED"}
        {tradingStatus.time_remaining != null && ` | ${Math.round(tradingStatus.time_remaining)}s left`}
        {tradingStatus.symbols?.length > 0 && ` | Symbols: ${tradingStatus.symbols.join(", ")}`}
      </div>

      <div className="admin-sections">
        {/* Case Management */}
        <div className="admin-section">
          <h4>Case</h4>
          <div className="admin-row">
            <label>Run #</label>
            <input
              type="number"
              min={1}
              max={5}
              value={runNumber}
              onChange={(e) => setRunNumber(Number(e.target.value))}
            />
            <button onClick={handleLoadCase}>Load Case</button>
          </div>
        </div>

        {/* Mini Case (Beauty Contest) */}
        <div className="admin-section">
          <h4>Beauty Contest (VWAP)</h4>
          <div className="admin-row">
            <label>Symbol</label>
            <input
              type="text"
              value={miniCaseSymbol}
              onChange={(e) => setMiniCaseSymbol(e.target.value)}
              style={{ maxWidth: 80 }}
            />
            <label>Duration</label>
            <input
              type="number"
              min={60}
              value={miniCaseDuration}
              onChange={(e) => setMiniCaseDuration(Number(e.target.value))}
            />
          </div>
          <div className="admin-row">
            <button className="admin-btn-green" onClick={handleLoadMiniCase}>Start Beauty Contest</button>
            <button onClick={handleSettleVwap}>Settle at 80% VWAP</button>
          </div>
        </div>

        {/* Role Assignment */}
        <div className="admin-section">
          <h4>Assign Roles</h4>
          <div className="admin-role-grid">
            {TRADER_IDS.map((uid) => (
              <div key={uid} className="admin-role-row">
                <span className="admin-role-uid">{uid}</span>
                <select
                  value={roleAssignments[uid] || ""}
                  onChange={(e) =>
                    setRoleAssignments((prev) => ({ ...prev, [uid]: e.target.value }))
                  }
                >
                  <option value="">—</option>
                  {ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="admin-row">
            <button onClick={handleAssignRoles}>Assign Manually</button>
            <button className="admin-btn-green" onClick={() => send("auto_assign_roles")}>Auto-Assign (Random)</button>
          </div>
        </div>

        {/* Symbols */}
        <div className="admin-section">
          <h4>Symbols</h4>
          <div className="admin-row">
            <input
              type="text"
              value={symbols}
              onChange={(e) => setSymbols(e.target.value)}
              placeholder="A,B,C,D,E"
            />
            <button onClick={handleSetSymbols}>Set</button>
          </div>
        </div>

        {/* Trading Controls */}
        <div className="admin-section">
          <h4>Trading</h4>
          <div className="admin-row">
            <label>Duration (s)</label>
            <input
              type="number"
              min={0}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
            />
            <button className="admin-btn-green" onClick={handleStartTrading}>Start</button>
            <button className="admin-btn-red" onClick={handleEndTrading}>End</button>
          </div>
        </div>

        {/* Draw Controls */}
        <div className="admin-section">
          <h4>Draws</h4>
          <div className="admin-row">
            <label>Interval (s)</label>
            <input
              type="number"
              min={1}
              value={drawInterval}
              onChange={(e) => setDrawInterval(Number(e.target.value))}
            />
            <button className="admin-btn-green" onClick={handleStartDraws}>Start Draws</button>
            <button className="admin-btn-red" onClick={handleStopDraws}>Stop Draws</button>
          </div>
        </div>

        {/* Bot Control */}
        <div className="admin-section">
          <h4>Random Taker Bot {botRunning ? "🟢" : "🔴"}</h4>
          <div className="admin-row">
            <button className="admin-btn-green" onClick={() => send("start_bot")}>Start Bot</button>
            <button className="admin-btn-red" onClick={() => send("stop_bot")}>Stop Bot</button>
          </div>
        </div>

        {/* Settlement */}
        <div className="admin-section">
          <h4>Settlement</h4>
          <div className="admin-row">
            <input
              type="text"
              value={settlementInput}
              onChange={(e) => setSettlementInput(e.target.value)}
              placeholder="A=70 B=30 C=15"
            />
            <button onClick={handleSettle}>Manual Settle</button>
          </div>
          <div className="admin-row">
            <button onClick={handleSettleCase}>Settle from Case</button>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="admin-section">
          <h4>Leaderboard</h4>
          <div className="admin-row">
            <button onClick={handleGetLeaderboard}>Refresh</button>
          </div>
          {leaderboard && (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>#</th><th>Trader</th><th>PnL</th><th>Cash</th><th>Trades</th></tr>
                </thead>
                <tbody>
                  {leaderboard.map((e) => (
                    <tr key={e.user_id}>
                      <td>{e.rank}</td>
                      <td>{e.name}</td>
                      <td className={e.pnl > 0 ? "admin-positive" : e.pnl < 0 ? "admin-negative" : ""}>
                        {e.pnl > 0 ? "+" : ""}${e.pnl.toFixed(0)}
                      </td>
                      <td>${e.final_cash.toFixed(0)}</td>
                      <td>{e.matched_trades}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Multi-Quarter (Basketball) */}
        <div className="admin-section">
          <h4>Multi-Quarter Leaderboard</h4>
          <div className="admin-row">
            <label>Quarter</label>
            <input
              type="number"
              min={1}
              max={4}
              value={quarterNum}
              onChange={(e) => setQuarterNum(Number(e.target.value))}
              style={{ maxWidth: 60 }}
            />
            <button onClick={() => send("save_quarter", { quarter: quarterNum })}>Save Q{quarterNum}</button>
            <button className="admin-btn-green" onClick={() => send("get_combined_leaderboard")}>Combined</button>
          </div>
          {combinedLeaderboard && (
            <div className="admin-table-wrap">
              <div style={{ fontSize: "11px", marginBottom: 4, opacity: 0.7 }}>
                Quarters included: {combinedLeaderboard.quarters.map(q => `Q${q}`).join(", ")}
              </div>
              <table className="admin-table">
                <thead>
                  <tr><th>#</th><th>Trader</th><th>Total PnL</th><th>Trades</th></tr>
                </thead>
                <tbody>
                  {combinedLeaderboard.leaderboard.map((e) => (
                    <tr key={e.team}>
                      <td>{e.rank}</td>
                      <td>{e.name}</td>
                      <td className={e.total_pnl > 0 ? "admin-positive" : e.total_pnl < 0 ? "admin-negative" : ""}>
                        {e.total_pnl > 0 ? "+" : ""}${e.total_pnl.toFixed(0)}
                      </td>
                      <td>{e.total_trades}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Teams Management */}
        <div className="admin-section">
          <h4>Teams</h4>
          <div className="admin-row">
            <button onClick={handleGetTeams}>Refresh Teams</button>
            <button className="admin-btn-green" onClick={handleSaveTeams}>Save Teams</button>
          </div>
          {teams.map((team) => (
            <div key={team.team_id} className="admin-team-row">
              <span className="admin-team-label">{team.name} ({team.team_id})</span>
              {(pendingTeams[team.team_id] || []).map((member, idx) => (
                <select
                  key={idx}
                  value={member}
                  onChange={(e) => handleTeamMemberChange(team.team_id, idx, e.target.value)}
                >
                  {TRADER_IDS.map((uid) => (
                    <option key={uid} value={uid}>{uid}</option>
                  ))}
                </select>
              ))}
            </div>
          ))}
        </div>

        {/* Portfolio Viewer */}
        <div className="admin-section">
          <h4>View Portfolio</h4>
          <div className="admin-row">
            <select
              value={portfolioUserId}
              onChange={(e) => setPortfolioUserId(e.target.value)}
            >
              {teams.length > 0
                ? teams.map((t) => (
                    <option key={t.team_id} value={t.team_id}>{t.name} ({t.team_id})</option>
                  ))
                : TRADER_IDS.map((uid) => (
                    <option key={uid} value={uid}>{uid}</option>
                  ))
              }
            </select>
            <button onClick={handleGetPortfolio}>View</button>
          </div>
          {viewedPortfolio && (
            <div className="admin-portfolio-view">
              <div><strong>{viewedPortfolio.name}</strong> ({viewedPortfolio.user_id})</div>
              <div>Cash: ${viewedPortfolio.cash.toFixed(0)} (locked: ${viewedPortfolio.locked_cash.toFixed(0)})</div>
              <div className={viewedPortfolio.pnl > 0 ? "admin-positive" : viewedPortfolio.pnl < 0 ? "admin-negative" : ""}>
                PnL: {viewedPortfolio.pnl > 0 ? "+" : ""}${viewedPortfolio.pnl.toFixed(0)}
              </div>
              <div>Active Orders: {viewedPortfolio.active_orders} | Trades: {viewedPortfolio.matched_trades}</div>
              {Object.keys(viewedPortfolio.positions).length > 0 && (
                <div>Positions: {Object.entries(viewedPortfolio.positions).map(([sym, qty]) =>
                  `${sym}: ${qty > 0 ? "+" : ""}${qty}`
                ).join(", ")}</div>
              )}
            </div>
          )}
        </div>

        {/* Reset */}
        <div className="admin-section">
          <h4>Session</h4>
          <div className="admin-row">
            <button className="admin-btn-red" onClick={handleResetSession}>Reset Session</button>
          </div>
        </div>

        {/* Log */}
        <div className="admin-section">
          <h4>Log</h4>
          <div className="admin-log">
            {log.map((entry, i) => (
              <div key={i} className="admin-log-entry">{entry}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
